// ==================== RECENT ACTIVITY COMPONENT ====================

import { motion } from 'framer-motion';
import { 
  Send, 
  MessageSquare, 
  CheckCircle2, 
  XCircle, 
  Users, 
  Bot,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { RecentActivityItem } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface RecentActivityProps {
  activities: RecentActivityItem[];
  isLoading?: boolean;
}

// ==================== ACTIVITY CONFIG ====================

const ACTIVITY_CONFIG: Record<string, { icon: LucideIcon; color: string; bgColor: string }> = {
  message_sent: { icon: Send, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  message_received: { icon: MessageSquare, color: 'text-green-400', bgColor: 'bg-green-500/10' },
  campaign_started: { icon: Zap, color: 'text-(--color-signal-orange)', bgColor: 'bg-(--color-signal-orange)/10' },
  campaign_completed: { icon: CheckCircle2, color: 'text-green-400', bgColor: 'bg-green-500/10' },
  template_approved: { icon: CheckCircle2, color: 'text-green-400', bgColor: 'bg-green-500/10' },
  template_rejected: { icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/10' },
  contact_added: { icon: Users, color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  bot_triggered: { icon: Bot, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10' },
};

// ==================== HELPER ====================

const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
};

// ==================== COMPONENT ====================

export const RecentActivity = ({ activities, isLoading = false }: RecentActivityProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-2 animate-pulse">
                  <div className="w-7 h-7 rounded-full bg-(--color-border-gray)" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 w-28 bg-(--color-border-gray) rounded" />
                    <div className="h-2.5 w-18 bg-(--color-border-gray) rounded" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {activities.length === 0 ? (
            <div className="py-4 text-center">
              <MessageSquare size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
              <p className="text-xs text-(--color-muted-gray)">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-1">
              {activities.map((activity) => {
                const config = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.message_sent;
                const Icon = config.icon;
                
                return (
                  <div
                    key={activity.id}
                    className="flex items-start gap-2 py-1.5 border-b border-(--color-border-gray) last:border-b-0"
                  >
                    <div className={cn('w-6 h-6 rounded-full flex items-center justify-center shrink-0', config.bgColor)}>
                      <Icon size={12} className={config.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate">{activity.title}</p>
                      <p className="text-[10px] text-(--color-muted-gray) truncate">{activity.description}</p>
                    </div>
                    <span className="text-[10px] text-(--color-muted-gray) shrink-0">
                      {formatTimeAgo(activity.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RecentActivity;
